import { Component, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd, Router, Event, NavigationStart } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FeatureNames } from './shared/models/feature-names.enum';
import { FeaturesStatusService } from './shared/services/util/features-status.service';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { environment } from '../environments/environment'
import { TranslationConstants } from './shared/services/util/translation.constants';
import { UXConfigsService } from './shared/services/util/ux-configs.service';
import { AppService } from './app.service';
import { MatIconRegistry } from '@angular/material/icon';
import { AccountService } from './shared/services/account/account.services';
import { FintechAccountStatus } from './shared/models/BankingAccount';
import { HomeService } from './shared/services/account/home.service';
import { LocalStorageService } from 'angular-web-storage';
import { LocalStorageKeys } from './shared/utils/local.storage.keys';
import { OneSignal } from 'onesignal-ngx';
import { MatDialog } from '@angular/material/dialog';
import { DeviceLoginWarningModalComponent } from './shared/device-login-warning-modal/device-login-warning-modal.component';
import { CountryAlertModalComponent } from './shared/country-alert-modal/country-alert-modal.component';
import { GeolocationService } from './shared/services/geolocation/geolocation.service';
import { CountryAlertsService, ICountryAlert } from './shared/services/apis/country-alerts.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    private static readonly COUNTRY_ALERT_SESSION_KEY = 'countryAlertShown';

    public title: string = 'Midas';
    public showBottom: boolean = false;
    public hideExternalHeader: boolean = false;
    public hideBottom: boolean = false;
    public hideExternalFooter: boolean = true;
    public viewComponent: boolean = false;
    public svgIcons: Array<string> = [
        'apple',
        'arrow-down',
        'bank-slip',
        'benefits',
        'consortium',
        'chat-no-user',
        'deposits',
        'document-key',
        'draft',
        'escrow',
        'forward',
        'google-play',
        'pix',
        'random-key',
        'transfers',
        'loan',
        'linkedin',
        'pix-copyandpaste',
        'pix-history',
        'pix-keys',
        'pix-qrcode',
        'pix-receive',
        'pix-transfer',
        'pixfix',
        'safe',
        'swap',
        'health',
        'instagram',
        'twitter',
        'back-side-document',
        'front-side-document',
        'selfie-with-document',
        "blockchain-logo",
        "stamp-sos",
        "whatsapp",
        "message",
        "edit",
        'bridge',
        'arrow_swap',
        'transfer-token',
        'receive-token'
    ];

    public oneSignal: OneSignal = new OneSignal();

    constructor(
        private readonly translate: TranslateService,
        private readonly router: Router,
        private readonly featuresStatusService: FeaturesStatusService,
        private readonly translatableService: TranslationConstants,
        private readonly storage: LocalStorageService,
        private readonly uxConfigsService: UXConfigsService,
        private readonly titleService: Title,
        private readonly appService: AppService,
        private readonly matIconRegistry: MatIconRegistry,
        private readonly homeService: HomeService,
        private readonly domSanitizer: DomSanitizer,
        private readonly renderer: Renderer2,
        private readonly accountService: AccountService,
        private readonly featureStatusService: FeaturesStatusService,
        private readonly dialog: MatDialog,
        private readonly geolocationService: GeolocationService,
        private readonly countryAlertsService: CountryAlertsService
    ) {
        const pageTitle = this.resolvePageTitle();
        this.title = pageTitle;
        this.titleService.setTitle(pageTitle);


        this.matIconRegistry.addSvgIcon(
            "blockchain",
            this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/svgIcons/blockchain_icon.svg")
        );

        this.matIconRegistry.addSvgIcon("en", this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/language/en.svg"));
        this.matIconRegistry.addSvgIcon("pt-br", this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/language/pt-br.svg"));
        this.matIconRegistry.addSvgIcon("es", this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/flagIcons/spa_flag.svg"));

        this.translate.addLangs(['en', 'es', 'fr', 'pt-br', 'eninternal', 'pt-brinternal']);
        const configuredLanguageDefault: string = String((environment as any)?.languageDefault).toLowerCase();
        const defaultLanguage: string = ['pt-br', 'en', 'es'].includes(configuredLanguageDefault)
            ? configuredLanguageDefault
            : 'pt-br';

        this.translate.setDefaultLang(defaultLanguage);
        this.translate.use(defaultLanguage);


        let displayMobile = (window.innerWidth || window.screen.width) <= 768;
        if (displayMobile) {
            this.showBottom = true;
        }

        this.translatableService.getTexts().subscribe();

        this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationEnd) {
                const currentUrl: string = event?.url;
                this.appService.setCurrentUrl(currentUrl);

                if (currentUrl?.includes('navigation') ||
                    currentUrl?.includes('digital-banking/new-account') ||
                    currentUrl?.includes('digital-banking/external') ||
                    currentUrl?.includes('antifraud-approval') ||
                    currentUrl?.includes('marketplace') ||
                    currentUrl === '/' ||
                    currentUrl?.includes('events') ||
                    currentUrl?.includes('tokenization')
                ) {
                    this.hideExternalHeader = true;
                } else {
                    this.hideExternalHeader = false;
                }

                if (
                    currentUrl?.includes('digital-banking/new-account') ||
                    currentUrl?.includes('digital-banking/external') ||
                    currentUrl?.includes('antifraud-approval') ||
                    currentUrl === '/' ||
                    currentUrl?.includes('account')
                ) {
                    this.hideBottom = true;
                } else {
                    this.hideBottom = false;
                }

                if (currentUrl?.includes('digital-banking/new-account') ||
                    currentUrl?.includes('digital-banking/external') ||
                    currentUrl?.includes('antifraud-approval') ||
                    currentUrl?.includes('marketplace') ||
                    currentUrl?.includes('account') ||
                    currentUrl === '/' ||
                    currentUrl?.includes('events') ||
                    currentUrl?.includes('tokenization')
                ) {
                    this.hideExternalFooter = true;
                } else {
                    this.hideExternalFooter = false;
                }
                this.checkHideHeaderAndFooter(currentUrl);
            }

            if (event instanceof NavigationStart) {
                const destinyRoute: string = event.url;
                const originRoute: string = this.router.url;

                if ( (originRoute === '/account/login' &&
                   !destinyRoute?.includes('account'))) {
                    this.onExitLogin();
                }
            }
        });
        this.registrySvgIcons();
        this.renderer.addClass(document.body, 'custom-scrollbar');


        this.uxConfigsService.getOrderOfHomeSections().subscribe(data => {
            if (data && data?.length > 0) {
                this.homeService.sectionList = data;
            }
            this.homeService.loaded = true;
        });

    }

    public ngOnInit(): void {
        this.featuresStatusService.getAllFeatureStatus().subscribe(features => {
            for (let feature of Object.keys(features)) {
                this.storage.set(LocalStorageKeys.FEATURE_STATUS + feature, features[feature]);

                if (feature === FeatureNames.ONE_SIGNAL) {
                    this.oneSignal.init({
                        appId: environment.oneSignalId,
                        serviceWorkerParam: {
                            scope: '/onesignal'
                        }
                    });
                }
            }
        });
        this.uxConfigsService.getEniatoHeaderLinks(true).subscribe();
        this.router.events.subscribe((evt) => {
            if (!(evt instanceof NavigationEnd)) {
                return;
            }
            window.scrollTo(0, 0);
        });

        this.accountService.verifyAuthentication().subscribe(isAuthenticated => {
            if (isAuthenticated) {
                this.checkCountryAlerts();
            }
        });
    }

    public registrySvgIcons(): void {
        this.svgIcons.forEach((imageName: string) => {
            this.matIconRegistry.addSvgIcon(
                imageName,
                this.domSanitizer.bypassSecurityTrustResourceUrl(`../assets/svg-icons/${imageName}.svg`)
            );
        });
    }

    public checkHideHeaderAndFooter(currentUrl: string): void {
        this.accountService.getLoggedUserDetails().subscribe(user => {
            if (user) {
                this.viewComponent = true;
            } else {
                this.viewComponent = false;
            }
        });
    }

    private onExitLogin(): void {
        let deviceManagementEnabled: boolean = false;
        this.featureStatusService.getFeatureStatus(FeatureNames.DEVICE_MANAGEMENT).subscribe(data => {
            deviceManagementEnabled = data;
        });

        let hasCurrentDevice: boolean = false;
        this.accountService.getDevicesByUser().subscribe(resp => {
            hasCurrentDevice = resp.some(device => device?.isCurrentDevice);

            if (deviceManagementEnabled && !hasCurrentDevice) {
                this.dialog.open(DeviceLoginWarningModalComponent, {
                    panelClass: ['device-login-warning-modal', 'custom-modal'],
                    maxWidth: '600px'
                });
            }
        });

        this.checkCountryAlerts();
    }

    private checkCountryAlerts(): void {
        if (sessionStorage.getItem(AppComponent.COUNTRY_ALERT_SESSION_KEY) === 'true') {
            return;
        }

        this.countryAlertsService.getCountryAlerts().subscribe((alerts: Array<ICountryAlert>) => {
            if (!alerts || alerts.length === 0) {
                return;
            }

            this.geolocationService.getLocationData().subscribe(geo => {
                const userCountry = (geo?.country || '').toString().trim().toUpperCase();
                if (!userCountry) {
                    return;
                }

                const match = alerts.find(a => (a?.country || '').toString().trim().toUpperCase() === userCountry);
                if (!match) {
                    return;
                }

                if (sessionStorage.getItem(AppComponent.COUNTRY_ALERT_SESSION_KEY) === 'true') {
                    return;
                }
                sessionStorage.setItem(AppComponent.COUNTRY_ALERT_SESSION_KEY, 'true');

                this.dialog.open(CountryAlertModalComponent, {
                    panelClass: ['country-alert-modal', 'custom-modal'],
                    maxWidth: '600px',
                    data: {
                        country: match.country,
                        title: match.title,
                        alert: match.alert
                    }
                });
            });
        });
    }

    private resolvePageTitle(): string {
        const runtimeConfig = (window as any).__APP_CONFIG__;
        const runtimePageTitle = typeof runtimeConfig?.pageTitle === 'string'
            ? runtimeConfig.pageTitle.trim()
            : '';

        if (runtimePageTitle) {
            return runtimePageTitle;
        }

        const staticPageTitle = typeof document?.title === 'string'
            ? document.title.trim()
            : '';

        if (staticPageTitle && staticPageTitle !== 'Midas Web') {
            return staticPageTitle;
        }

        if (environment?.pageTitle && environment.pageTitle !== '__PAGE_TITLE__') {
            return environment.pageTitle;
        }

        return 'Midas Web';
    }
}
